import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { indexerTradeToMarketTrade, type IndexerTradeItem } from '../pages/markets/api/sidebarTradeTypes';
import { shortAccount } from '../pages/markets/utils/traderFormat';
import { Badge } from '../pages/markets/components/shared';
import type { AlertItem } from '../pages/markets/api/types';

// Same shape as AlertRow's timestamp (the other notifications): time-only for
// today, "Mon D HH:MM:SS" otherwise.
function formatTimestamp(d: Date): string {
    const now = new Date();
    const isToday = d.getFullYear() === now.getFullYear()
        && d.getMonth() === now.getMonth()
        && d.getDate() === now.getDate();
    const time = d.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
    if (isToday) return time;
    const date = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return `${date} ${time}`;
}

// Full dollar amount (not abbreviated like the sidebar's formatVolume).
function fullUsd(n: number): string {
    return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * Renders a trader_trade alert. The whole row navigates to the event (handled by
 * NotificationBell); the trader address is a separate click target for the
 * trader view via `onOpenTrader`.
 */
export function TraderTradeNotificationRow({ alert, onOpenTrader }: { alert: AlertItem; onOpenTrader?: () => void }) {
    const theme = useTheme();
    const ctx = (alert.context ?? {}) as { trader_address?: string; trade?: IndexerTradeItem };
    const addr = ctx.trader_address;

    if (!ctx.trade) {
        // Defensive fallback: render the plain title if the trade object is absent.
        return (
            <Box sx={{ p: 1 }}>
                <Typography variant="body2">{alert.title}</Typography>
                <Typography variant="caption" color="text.secondary">{alert.detail}</Typography>
            </Box>
        );
    }

    const trade = indexerTradeToMarketTrade(ctx.trade);
    // The notification outbox augments alerts with the real `event_title` (the
    // market question, resolved by the producer); the static AlertItem type
    // doesn't declare it, so read it off the augmented shape.
    const eventTitle = (alert as AlertItem & { event_title?: string }).event_title;
    const isBuy = trade.side === 'buy';
    const isYes = trade.outcomeIndex === 0;
    const outcomeKnown = trade.outcomeIndex !== null;

    return (
        <Box sx={{ px: 1.25, py: 0.75 }}>
            {/* Line 1: subject (address plain, no link) + full timestamp */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    {addr ? shortAccount(addr) : 'Trader'} traded{alert.is_simulated ? ' · simulated' : ''}
                </Typography>
                <Typography sx={{ fontSize: '10px', color: 'text.disabled', fontFamily: 'monospace', flexShrink: 0 }}>
                    {formatTimestamp(trade.timestamp)}
                </Typography>
            </Box>

            {/* Line 2: full event name — clicking the row opens the event view */}
            {eventTitle && (
                <Typography
                    sx={{
                        fontSize: '11px',
                        fontWeight: 500,
                        color: 'text.primary',
                        mt: 0.25,
                        cursor: 'pointer',
                        '&:hover': { color: 'primary.main', textDecoration: 'underline' },
                    }}
                >
                    {eventTitle}
                </Typography>
            )}

            {/* Line 3: side badge @price $amount  ········  clickable trader address */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.4 }}>
                <Badge color={isBuy ? theme.palette.success.main : theme.palette.error.main}>
                    {isBuy ? 'BUY' : 'SELL'}{outcomeKnown ? ` ${isYes ? 'YES' : 'NO'}` : ''}
                </Badge>
                <Typography component="span" sx={{ fontSize: '10px', color: 'text.secondary', fontFamily: 'monospace' }}>
                    @{trade.price.toFixed(2)}
                </Typography>
                <Typography component="span" sx={{ fontSize: '10px', fontFamily: 'monospace', color: 'text.primary' }}>
                    {fullUsd(trade.quoteAmount)}
                </Typography>
                {addr && (
                    <Typography
                        component="span"
                        onClick={e => { e.stopPropagation(); onOpenTrader?.(); }}
                        sx={{
                            ml: 'auto',
                            fontSize: '9px',
                            fontFamily: 'monospace',
                            color: 'text.disabled',
                            cursor: 'pointer',
                            '&:hover': { color: 'primary.main' },
                        }}
                        title="Open trader view"
                    >
                        {shortAccount(addr)}
                    </Typography>
                )}
            </Box>
        </Box>
    );
}
